import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { map, Observable, of, Subscription, switchMap, take, tap, timer } from 'rxjs';
import {
  AMCF_TAB_CHANNEL,
  AMCF_TAB_CLAIM_REPLY_MIN_MS,
  AMCF_TAB_MESSAGE_TAB_CLAIM,
  AMCF_TAB_MESSAGE_TAB_SUPERSEDED,
  AMCF_TAB_PEER_MAX_COUNT,
  AMCF_TAB_PEER_STALE_MS
} from './amcf-tab-coordinator.constants';
import type { AmcfTabMessage, AmcfTabVerdict, TabClaimPayload } from './amcf-tab-coordinator.models';
// import { tryCloseBrowserTab } from './amcf-tab-coordinator-close.util';
// import { isLocalDevTabShortcut } from './amcf-tab-coordinator-host.util';
import { parseTabMessage } from './amcf-tab-coordinator-parse.util';
import { parseTabStorageEvent, writeTabMessageToStorage } from './amcf-tab-coordinator-transport.util';
import { isClaimWinner, pickWinnerClaim } from './amcf-tab-coordinator.util';

@Injectable({ providedIn: 'root' })
export class AmcfTabCoordinatorService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly tabId = crypto.randomUUID();
  private readonly openedAt = Date.now();
  private readonly ownClaim: TabClaimPayload = { tabId: this.tabId, openedAt: this.openedAt };

  private channel: BroadcastChannel | null = null;
  private winner: TabClaimPayload = this.ownClaim;
  private readonly peerClaims = new Map<string, TabClaimPayload>();
  private readonly peerLastSeenAt = new Map<string, number>();
  private readonly lastClaimReplyAt = new Map<string, number>();
  private lastVerdict: AmcfTabVerdict | null = null;
  private storageListener: ((event: StorageEvent) => void) | null = null;
  private pageshowListener: ((event: PageTransitionEvent) => void) | null = null;
  private lifecycleInstalled = false;
  private destroyHookInstalled = false;
  private claimSubscription: Subscription | null = null;

  /** True se questa scheda è stata sostituita e il browser non l'ha chiusa. */
  readonly isSuperseded = signal(false);

  private get resolveMs(): number {
    return environment.AMCF_TAB_RESOLVE_MS ?? 300;
  }

  private get enabled(): boolean {
    return environment.AMCF_TAB_COORDINATOR_ENABLED === true;
  }

  /**
   * Coordina le tab sullo stesso origin.
   * Resta la scheda più nuova; le altre tentano close() e mostrano l'overlay se resta aperte.
   */
  claimActiveTab(): Observable<AmcfTabVerdict> {
    if (!this.enabled) {
      this.lastVerdict = 'stay';
      return of('stay');
    }

    this.ensureChannel();
    this.ensureStorageBridge();
    this.ensureLifecycleListeners();
    this.ensureDestroyHook();

    this.winner = this.ownClaim;
    this.clearPeerState();
    this.postClaim(this.ownClaim);

    return timer(this.resolveMs).pipe(
      switchMap(() => this.discoveryPhaseAfterWait()),
      take(1),
      tap(() => this.applyInitialVerdict())
    );
  }

  /**
   * Se nella prima finestra non arrivano peer, ripete il claim: le tab già attive rispondono
   * con il proprio TAB_CLAIM.
   */
  private discoveryPhaseAfterWait(): Observable<AmcfTabVerdict> {
    if (this.conflictPeers.length > 0) {
      return of(this.resolveInitialVerdict());
    }
    this.postClaim(this.ownClaim);
    return timer(this.resolveMs).pipe(map(() => this.resolveInitialVerdict()));
  }

  private get conflictPeers(): TabClaimPayload[] {
    return [...this.peerClaims.values()];
  }

  private resolveInitialVerdict(): AmcfTabVerdict {
    if (this.conflictPeers.length === 0) {
      return 'stay';
    }
    return isClaimWinner(this.ownClaim, this.winner) ? 'stay' : 'close';
  }

  private applyInitialVerdict(): void {
    // Per ora non chiudiamo né blocchiamo le schede (né overlay, né window.close).
    this.lastVerdict = 'stay';
    return;

    // if (this.isSuperseded()) {
    //   this.lastVerdict = 'close';
    //   return;
    // }
    //
    // if (isLocalDevTabShortcut()) {
    //   this.lastVerdict = 'stay';
    //   return;
    // }
    //
    // const verdict = this.resolveInitialVerdict();
    // this.lastVerdict = verdict;
    //
    // if (verdict === 'stay' && this.conflictPeers.length > 0) {
    //   this.notifySupersededTabs();
    //   return;
    // }
    //
    // if (verdict === 'close') {
    //   this.markSupersededAndClose();
    // }
  }

  private markSupersededAndClose(): void {
    // Disabilitato: non impostare overlay e non chiudere la scheda.
    return;
    // if (this.isSuperseded()) {
    //   return;
    // }
    // this.isSuperseded.set(true);
    // tryCloseBrowserTab();
  }

  private clearPeerState(): void {
    this.peerClaims.clear();
    this.peerLastSeenAt.clear();
    this.lastClaimReplyAt.clear();
  }

  private recordPeerClaim(claim: TabClaimPayload, now = Date.now()): void {
    this.peerClaims.set(claim.tabId, claim);
    this.peerLastSeenAt.set(claim.tabId, now);
    this.prunePeerClaims(now);
  }

  private prunePeerClaims(now: number): void {
    for (const [tabId, lastSeen] of this.peerLastSeenAt) {
      if (now - lastSeen > AMCF_TAB_PEER_STALE_MS) {
        this.peerClaims.delete(tabId);
        this.peerLastSeenAt.delete(tabId);
        this.lastClaimReplyAt.delete(tabId);
      }
    }

    while (this.peerClaims.size > AMCF_TAB_PEER_MAX_COUNT) {
      let evictId: string | null = null;
      let oldestSeen = Number.POSITIVE_INFINITY;
      for (const [tabId, lastSeen] of this.peerLastSeenAt) {
        if (lastSeen < oldestSeen) {
          oldestSeen = lastSeen;
          evictId = tabId;
        }
      }
      if (!evictId) {
        break;
      }
      this.peerClaims.delete(evictId);
      this.peerLastSeenAt.delete(evictId);
      this.lastClaimReplyAt.delete(evictId);
    }
  }

  private canReplyToPeerClaim(peerTabId: string, isDuplicateClaim: boolean, now = Date.now()): boolean {
    const lastReply = this.lastClaimReplyAt.get(peerTabId);
    if (lastReply != null && now - lastReply < AMCF_TAB_CLAIM_REPLY_MIN_MS) {
      return false;
    }
    if (isDuplicateClaim && lastReply != null) {
      return false;
    }
    return true;
  }

  private ensureChannel(): void {
    if (this.channel || typeof BroadcastChannel === 'undefined') {
      return;
    }
    try {
      this.channel = new BroadcastChannel(AMCF_TAB_CHANNEL);
    } catch {
      this.channel = null;
      return;
    }
    this.channel.onmessage = (ev: MessageEvent) => {
      this.dispatchInbound(parseTabMessage(ev.data));
    };
  }

  private ensureDestroyHook(): void {
    if (this.destroyHookInstalled) {
      return;
    }
    this.destroyHookInstalled = true;
    this.destroyRef.onDestroy(() => {
      this.claimSubscription?.unsubscribe();
      this.claimSubscription = null;
      this.channel?.close();
      this.channel = null;
      this.teardownBridges();
    });
  }

  private ensureStorageBridge(): void {
    if (this.storageListener || typeof window === 'undefined') {
      return;
    }
    this.storageListener = (event: StorageEvent) => {
      this.dispatchInbound(parseTabStorageEvent(event));
    };
    window.addEventListener('storage', this.storageListener);
  }

  private ensureLifecycleListeners(): void {
    if (this.lifecycleInstalled || typeof window === 'undefined') {
      return;
    }
    this.lifecycleInstalled = true;
    this.pageshowListener = (event: PageTransitionEvent) => {
      if (event.persisted && this.enabled) {
        this.isSuperseded.set(false);
        this.lastVerdict = null;
        this.claimSubscription?.unsubscribe();
        this.claimSubscription = this.claimActiveTab().subscribe();
      }
    };
    window.addEventListener('pageshow', this.pageshowListener);
  }

  private teardownBridges(): void {
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
    if (this.pageshowListener) {
      window.removeEventListener('pageshow', this.pageshowListener);
      this.pageshowListener = null;
    }
    this.lifecycleInstalled = false;
  }

  private dispatchInbound(parsed: AmcfTabMessage | null): void {
    if (!parsed) {
      return;
    }
    this.handleInbound(parsed);
  }

  private postClaim(claim: TabClaimPayload): void {
    this.broadcast({
      type: AMCF_TAB_MESSAGE_TAB_CLAIM,
      tabId: claim.tabId,
      openedAt: claim.openedAt
    });
  }

  private broadcast(message: AmcfTabMessage): void {
    this.channel?.postMessage(message);
    writeTabMessageToStorage(message);
  }

  private shouldReplyToIncomingClaim(): boolean {
    return this.lastVerdict === 'stay' || this.lastVerdict === null;
  }

  private handleInbound(msg: AmcfTabMessage): void {
    if (msg.type === AMCF_TAB_MESSAGE_TAB_CLAIM) {
      this.handleInboundTabClaim(msg.tabId, msg.openedAt);
      return;
    }

    if (msg.type === AMCF_TAB_MESSAGE_TAB_SUPERSEDED) {
      // Per ora ignora TAB_SUPERSEDED (non chiudere / non overlay).
      return;
      // if (msg.winnerTabId === this.tabId || isLocalDevTabShortcut()) {
      //   return;
      // }
      // const announced: TabClaimPayload = { tabId: msg.winnerTabId, openedAt: msg.winnerOpenedAt };
      // if (isClaimWinner(this.ownClaim, announced)) {
      //   return;
      // }
      // this.lastVerdict = 'close';
      // this.markSupersededAndClose();
    }
  }

  private handleInboundTabClaim(tabId: string, openedAt: number): void {
    if (tabId === this.tabId) {
      return;
    }
    const now = Date.now();
    const incoming: TabClaimPayload = { tabId, openedAt };
    const existing = this.peerClaims.get(tabId);
    const isDuplicateClaim = existing?.openedAt === openedAt;

    this.recordPeerClaim(incoming, now);
    this.winner = pickWinnerClaim(this.winner, incoming);

    // const iAmWinner = isClaimWinner(this.ownClaim, this.winner);

    // Per ora non chiudere le schede perdenti e non notificare TAB_SUPERSEDED.
    // if (this.lastVerdict === 'stay' && !iAmWinner && !isLocalDevTabShortcut()) {
    //   this.lastVerdict = 'close';
    //   this.markSupersededAndClose();
    //   return;
    // }
    //
    // if (!isDuplicateClaim && this.lastVerdict === 'stay' && iAmWinner && !isLocalDevTabShortcut()) {
    //   this.notifySupersededTabs();
    // }

    if (this.shouldReplyToIncomingClaim() && this.canReplyToPeerClaim(tabId, isDuplicateClaim, now)) {
      this.postClaim(this.ownClaim);
      this.lastClaimReplyAt.set(tabId, now);
    }
  }

  // private notifySupersededTabs(): void {
  //   this.broadcast({
  //     type: AMCF_TAB_MESSAGE_TAB_SUPERSEDED,
  //     winnerTabId: this.ownClaim.tabId,
  //     winnerOpenedAt: this.ownClaim.openedAt
  //   });
  // }
}
